import React from "react";
import { AlertTriangle } from "lucide-react";
import { AmplifyLogo } from "@/components/AmplifyLogo";

// Helper functions for formatting
const fmt = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

// Helper to determine input field class based on state
const getInputClassName = (isMonopolistic, isExceeded, isDanger, isWarning, isClient = false) => {
  const base = "w-20 h-6 rounded border px-2 text-xs text-right font-mono focus:outline-none focus:ring-2 focus:ring-[#3c68ff]";
  if (isMonopolistic) return `${base} border-purple-300 bg-purple-50 cursor-not-allowed`;
  if (isClient) return `${base} border-amber-300 bg-amber-50 cursor-not-allowed`;
  if (isExceeded || isDanger) return `${base} border-red-400 bg-red-50`;
  if (isWarning) return `${base} border-yellow-500 bg-yellow-50`;
  return `${base} border-slate-200`;
};

// Helper to determine discount badge class
const getDiscountBadgeClass = (isExceeded, isDanger, isWarning) => {
  const base = "text-xs font-medium px-1.5 py-0.5 rounded";
  if (isExceeded || isDanger) return `${base} bg-red-100 text-red-700`;
  if (isWarning) return `${base} bg-yellow-200 text-yellow-800`;
  return `${base} bg-emerald-100 text-emerald-700`;
};

// Helper to get discount badge tooltip
const getDiscountTooltip = (isExceeded, isDanger, isWarning, maxPct, costRate) => {
  if (isExceeded) return `Max ${maxPct}% discount for non-admin users`;
  if (isDanger) return `At or below cost rate (${costRate}%)`;
  if (isWarning) return `Discounted but above cost (${costRate}%)`;
  return "Discount from standard rate";
};

export function ProposedCostsTable({
  lineItems,
  proposedItems,
  proposedRates,
  setProposedRates,
  proposedAdminFeeType,
  setProposedAdminFeeType,
  proposedAdminFeeValue,
  setProposedAdminFeeValue,
  proposedTotals,
  proposedAdminFee,
  proposedTotal,
  showAdminFeeWarning,
  setShowAdminFeeWarning,
  // Discount related
  hasExceededDiscounts,
  MAX_DISCOUNT_PERCENT,
  discountLevel,
  isAdmin,
  // Helper functions
  calculate,
  sutaBases,
  isClientState,
  MONOPOLISTIC_STATES,
  getStandardWcRate,
  getWcCostRate,
  getStandardSutaRate,
  getSutaCostRate,
  calcDiscountPct,
  updateStateSutaRate,
}) {
  return (
    <div className="border-2 border-[#3c68ff] rounded-xl shadow-sm overflow-hidden bg-blue-50/20" data-testid="proposed-costs-table">
      {/* Discount Warning Banner */}
      {hasExceededDiscounts && (
        <div className="px-5 py-3 bg-red-50 border-b border-red-200 flex items-center gap-2" data-testid="discount-exceeded-warning">
          <AlertTriangle size={16} className="text-red-500 shrink-0" />
          <span className="text-sm text-red-700">
            <strong>Discount limit exceeded:</strong> One or more rates have discounts above the {MAX_DISCOUNT_PERCENT}% maximum allowed for your level ({discountLevel}). Please adjust rates before saving.
          </span>
        </div>
      )}
      <div className="px-5 py-3 border-b border-blue-200 bg-blue-50/40">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold font-['Outfit'] text-[#3c68ff]">
            <AmplifyLogo size={20} className="inline-block mr-2 -mt-0.5" /> Amplify HR Proposed Costs
            {discountLevel !== 'admin' && (
              <span className="ml-2 text-xs font-normal text-slate-500 capitalize">({discountLevel}: Max {MAX_DISCOUNT_PERCENT}% discount)</span>
            )}
          </h2>
          <div className="flex items-center gap-3">
            <label className={`text-xs font-medium uppercase tracking-wider ${showAdminFeeWarning ? 'text-red-600' : 'text-slate-500'}`}>
              Proposed Admin Fee {showAdminFeeWarning && <span className="text-red-600">*</span>}
            </label>
            <select data-testid="proposed-admin-fee-type-select" value={proposedAdminFeeType} onChange={e => setProposedAdminFeeType(e.target.value)}
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-xs focus:outline-none focus:ring-2 focus:ring-[#3c68ff]">
              <option value="pepm">PEPM</option>
              <option value="percent">% of Payroll</option>
            </select>
            <input data-testid="proposed-admin-fee-value-input" type="number" step="0.01" value={proposedAdminFeeValue} 
              onChange={e => { setProposedAdminFeeValue(e.target.value); setShowAdminFeeWarning(false); }}
              placeholder={proposedAdminFeeType === "pepm" ? "$/mo/EE" : "%"}
              className={`w-24 h-8 rounded-lg border bg-white px-2 text-xs text-right focus:outline-none focus:ring-2 focus:ring-[#3c68ff] ${showAdminFeeWarning ? 'border-red-500 ring-2 ring-red-200' : 'border-slate-200'}`} />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-blue-50/60">
              <th className="px-3 py-2 text-left text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">State</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">WC Code</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">EEs</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">Gross Wages</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">WC Rate</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">Disc %</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">WC Premium</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">SUTA Rate</th>
              <th className="px-3 py-2 text-center text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">Disc %</th>
              <th className="px-3 py-2 text-right text-xs font-semibold text-[#3c68ff] uppercase tracking-wider">SUTA Tax</th>
            </tr>
          </thead>
          <tbody>
            {proposedItems.map(item => {
              const c = calculate(item, sutaBases);
              const isClient = isClientState(item.state);
              const isMonopolistic = MONOPOLISTIC_STATES.includes(item.state);
              
              // Get rates for discount calculations
              const standardWcRate = getStandardWcRate(item.state, item.wcCode);
              const wcCostRate = getWcCostRate(item.state, item.wcCode);
              const standardSutaRate = getStandardSutaRate(item.state);
              const sutaCostRate = getSutaCostRate(item.state);
              const proposedWcRate = proposedRates[item.id]?.wcRate ?? standardWcRate ?? 0;
              const proposedSutaRate = proposedRates[item.id]?.sutaRate ?? standardSutaRate ?? item.sutaRate ?? 0;
              
              const wcDiscount = standardWcRate ? calcDiscountPct(standardWcRate, proposedWcRate) : 0;
              const sutaDiscount = standardSutaRate ? calcDiscountPct(standardSutaRate, proposedSutaRate) : 0;
              
              // Determine badge status
              const wcAtOrBelowCost = wcCostRate != null && proposedWcRate <= wcCostRate;
              const sutaAtOrBelowCost = sutaCostRate != null && proposedSutaRate <= sutaCostRate;
              
              const wcExceeded = !isAdmin && standardWcRate && wcDiscount > MAX_DISCOUNT_PERCENT;
              const wcWarning = isAdmin && wcDiscount > 0 && !wcAtOrBelowCost;
              const wcDanger = isAdmin && wcAtOrBelowCost;
              
              const sutaExceeded = !isAdmin && !isClient && standardSutaRate && sutaDiscount > MAX_DISCOUNT_PERCENT;
              const sutaWarning = isAdmin && !isClient && sutaDiscount > 0 && !sutaAtOrBelowCost;
              const sutaDanger = isAdmin && !isClient && sutaAtOrBelowCost;
              
              return (
                <tr key={item.id} className="border-b border-blue-100 hover:bg-blue-50/30 transition-colors" data-testid={`proposed-row-${item.id}`}>
                  <td className="px-3 py-1.5 font-medium text-slate-900">
                    <div className="flex items-center gap-1.5">
                      {item.state}
                      {isMonopolistic && (
                        <span className="text-[9px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded" title="Monopolistic State - WC rate locked to current rate">
                          MONO
                        </span>
                      )}
                      {isClient && (
                        <span className="text-[9px] bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded" title="Client Reporting - SUTA rate locked to current rate">
                          CLIENT
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-1.5 font-mono text-slate-700">{item.wcCode}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{item.employeeCount}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{fmt(item.grossWages)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        data-testid={`proposed-wc-rate-${item.id}`}
                        type="number" step="0.01"
                        value={proposedRates[item.id]?.wcRate ?? ""}
                        onChange={e => setProposedRates(prev => ({
                          ...prev,
                          [item.id]: { ...prev[item.id], wcRate: parseFloat(e.target.value) || 0 }
                        }))}
                        disabled={isMonopolistic}
                        className={getInputClassName(isMonopolistic, wcExceeded, wcDanger, wcWarning)}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {isMonopolistic ? (
                      <span className="text-xs text-purple-500" title="Monopolistic state - rate must match current">—</span>
                    ) : standardWcRate && wcDiscount > 0 ? (
                      <span 
                        data-testid={`wc-discount-${item.id}`}
                        className={getDiscountBadgeClass(wcExceeded, wcDanger, wcWarning)}
                        title={getDiscountTooltip(wcExceeded, wcDanger, wcWarning, MAX_DISCOUNT_PERCENT, wcCostRate)}
                      >
                        -{wcDiscount}%
                      </span>
                    ) : wcDiscount < 0 ? (
                      <span className="text-xs text-slate-400">+{Math.abs(wcDiscount)}%</span>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono font-medium">{fmt(c.wcPremium)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <input
                        data-testid={`proposed-suta-rate-${item.id}`}
                        type="number" step="0.01"
                        value={proposedRates[item.id]?.sutaRate ?? ""}
                        onChange={e => {
                          const newRate = parseFloat(e.target.value) || 0;
                          updateStateSutaRate(item.state, newRate);
                        }}
                        disabled={isClient}
                        className={getInputClassName(false, sutaExceeded, sutaDanger, sutaWarning, isClient)}
                      />
                    </div>
                  </td>
                  <td className="px-3 py-1.5 text-center">
                    {isClient ? (
                      <span className="text-xs text-amber-500" title="Client reporting - rate must match current">—</span>
                    ) : standardSutaRate && sutaDiscount > 0 ? (
                      <span 
                        data-testid={`suta-discount-${item.id}`}
                        className={getDiscountBadgeClass(sutaExceeded, sutaDanger, sutaWarning)}
                        title={getDiscountTooltip(sutaExceeded, sutaDanger, sutaWarning, MAX_DISCOUNT_PERCENT, sutaCostRate)}
                      >
                        -{sutaDiscount}%
                      </span>
                    ) : sutaDiscount < 0 ? (
                      <span className="text-xs text-slate-400">+{Math.abs(sutaDiscount)}%</span>
                    ) : (
                      <span className="text-xs text-slate-300">—</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono font-medium">{fmt(c.sutaTax)}</td>
                </tr>
              );
            })}
            <tr className="bg-blue-50/60 font-semibold">
              <td className="px-3 py-2" colSpan={2}>Total</td>
              <td className="px-3 py-2 text-right font-mono">{proposedTotals.employees}</td>
              <td className="px-3 py-2 text-right font-mono">{fmt(proposedTotals.grossWages)}</td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-right font-mono">{fmt(proposedTotals.wcPremium)}</td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2"></td>
              <td className="px-3 py-2 text-right font-mono">{fmt(proposedTotals.sutaTax)}</td>
            </tr>
            {proposedAdminFee > 0 && (
              <tr className="bg-blue-50/40">
                <td className="px-3 py-1.5 text-xs text-[#3c68ff]/70" colSpan={6}>Admin Fee ({proposedAdminFeeType === "pepm" ? `$${proposedAdminFeeValue}/mo/EE` : `${proposedAdminFeeValue}% of payroll`})</td>
                <td className="px-3 py-1.5 text-right font-mono text-sm" colSpan={4}>{fmt(proposedAdminFee)}</td>
              </tr>
            )}
            <tr className="bg-blue-100/60 font-bold text-[#3c68ff]">
              <td className="px-3 py-2" colSpan={6}>Grand Total</td>
              <td className="px-3 py-2 text-right font-mono" colSpan={4}>{fmt(proposedTotal)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
